import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightLeft, SearchX, HelpCircle } from 'lucide-react';
import { createSectionTracker } from '../../services/funnelTracker';

const painCards = [
  {
    icon: ArrowRightLeft,
    title: 'Querés transicionar a Data Engineering pero no sabés cómo',
    description:
      'Hacés dashboards, reportes, análisis… y sentís que tocaste techo. Sabés que DE paga 2x o 3x, pero no tenés claro el camino para pasar de BI/Analytics a infraestructura de datos.',
  },
  {
    icon: SearchX,
    title: 'Mandás CVs y nadie te llama',
    description:
      'Estudiás hace meses, hacés cursos, ponés "Open to Work"… y nada. Las empresas piden Spark, Databricks y pipelines reales, y vos tenés certificados que no cuentan como experiencia.',
  },
  {
    icon: HelpCircle,
    title: 'No sabés por dónde empezar y estás paralizado',
    description:
      'Un tutorial de Python acá, un video de SQL allá, un curso de AWS que nunca terminaste. Sentís que aprendés de todo un poco pero no avanzás. Te falta un plan concreto.',
  },
];

export default function PainPoints() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tracker = createSectionTracker('pain_section');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            ¿Te sentís identificado?
          </h2>
          <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
            Si alguno de estos puntos te suena, no estás solo. El 90% de las personas que me escriben están exactamente ahí.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {painCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-900 to-gray-800 border border-red-500/20 rounded-2xl p-6 sm:p-8 hover:border-red-500/40 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 leading-tight">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {card.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
